import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import KPICard from "../KPICard/KPICard";
import { useMemberDashboardPage } from "../../hooks/useMemberDashboardPage";

/**
 * Member KPI dashboard — the SOAR KPIs in plain English.
 *
 * Sections 01 (Profit & Loss) and 02 (Balance Sheet) are wired today. Sales &
 * Operations renders through the same loop as its calculations land — a section
 * with no KPIs is skipped, so declaring one early leaves no empty heading.
 */

/**
 * Small amber label above a heading. This is the one place the page keeps the
 * prototype's uppercase-and-tracked treatment the design system otherwise
 * rules out — it reads as a kicker rather than a heading, and the page loses
 * its structure without it.
 */
const EYEBROW =
  "text-xs font-semibold uppercase tracking-[0.14em] text-amber-600 dark:text-amber-400";

/**
 * How many placeholder cards the grid holds while the request is in flight.
 * Tracks the number of wired KPIs so the skeleton has the shape of what
 * replaces it — the grid does not reflow as the real cards arrive.
 */
const SKELETON_CARDS = 10;

const MemberDashboardPage: React.FC = () => {
  const { sections, periodLabel, isLoading } = useMemberDashboardPage();

  return (
    <MainPageWrapper
      actions={
        periodLabel ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {periodLabel}
          </span>
        ) : undefined
      }
      title="Member Dashboard"
    >
      <div className="space-y-10">
        <section className="max-w-2xl">
          <p className={EYEBROW}>Your numbers</p>
          <h2 className="mt-3 text-4xl font-bold leading-[1.05] tracking-tight text-secondary dark:text-white sm:text-5xl">
            Your business, in plain English.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-gray-600 dark:text-gray-300">
            Every number below is pulled straight from your{" "}
            <strong className="font-semibold text-gray-900 dark:text-white">
              QuickBooks
            </strong>{" "}
            — no spreadsheets, no accounting degree. Each chart shows one bar
            per month, with the most recent completed month highlighted.{" "}
            <strong className="font-semibold text-gray-900 dark:text-white">
              Tap any card to see exactly how it&apos;s calculated.
            </strong>
          </p>
        </section>

        {isLoading && (
          <div className="grid items-start gap-6 [grid-template-columns:repeat(auto-fill,minmax(26rem,1fr))]">
            {Array.from({ length: SKELETON_CARDS }, (_, index) => (
              <div
                className="min-h-[32rem] animate-pulse rounded-lg border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800"
                key={index}
              >
                <div className="h-3 w-32 rounded bg-gray-200 dark:bg-slate-700" />
                <div className="mt-6 h-6 w-40 rounded bg-gray-200 dark:bg-slate-700" />
                <div className="mt-4 h-12 w-48 rounded bg-gray-200 dark:bg-slate-700" />
                <div className="mt-8 h-40 rounded bg-gray-100 dark:bg-slate-700/50" />
              </div>
            ))}
          </div>
        )}

        {sections.map((section) => (
          <section key={section.code}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                [{section.ordinal}]
              </span>
              <h2 className="text-2xl font-bold tracking-tight text-secondary dark:text-white">
                {section.title}
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {section.kpis.length}{" "}
                {section.kpis.length === 1 ? "metric" : "metrics"} · from{" "}
                {section.source}
              </span>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
              {section.blurb}
            </p>

            <div className="mt-6 grid items-start gap-6 [grid-template-columns:repeat(auto-fill,minmax(26rem,1fr))]">
              {section.kpis.map((kpi) => (
                <KPICard key={kpi.code} kpi={kpi} />
              ))}
            </div>
          </section>
        ))}

        {!isLoading && sections.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No KPIs are available yet. Once your QuickBooks accounts are mapped
            and confirmed, your numbers will appear here.
          </p>
        )}
      </div>
    </MainPageWrapper>
  );
};

export default MemberDashboardPage;
